import {
  Check,
  Award,
  Users,
  Globe2,
  Factory,
  Leaf,
  ShieldCheck,
  Recycle,
  Truck,
  Handshake,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

export const AboutPage = () => {
  return (
    <div className="bg-cream">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-cream via-cream/95 to-forest/5">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto space-y-6 text-center">
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-sand">
              About Reliable Exports
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-forest leading-tight">
              About <span className="text-sand">Reliable Exports</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Since 2006, Reliable Exports has been a trusted global partner for
              retailers, importers, and private labels seeking premium, eco-friendly
              jute and cotton bags. We blend craftsmanship, sustainability, and scale
              to deliver export-grade solutions for brands worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="bg-white/80 rounded-3xl shadow-card border border-sand/30 px-6 md:px-10 lg:px-12 py-10 md:py-12 relative overflow-hidden">
            <div className="pointer-events-none absolute -right-20 -top-20 w-56 h-56 rounded-full bg-forest/5 blur-3xl" />
            <div className="pointer-events-none absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-sand/5 blur-2xl" />

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)] gap-10 lg:gap-14 items-start relative">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-forest/5 border border-forest/10 text-xs font-medium tracking-[0.22em] uppercase text-forest">
                  <span className="w-1.5 h-1.5 rounded-full bg-sand" />
                  Export-Focused Manufacturer
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest">
                  A Manufacturing Partner Built for Global Brands
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Headquartered in India, Reliable Exports specializes in the
                  manufacture of export-quality jute and cotton bags for leading
                  retailers, promotional agencies, and private labels across the globe.
                  From concept and design to bulk production and export logistics, we
                  operate as an end-to-end manufacturing partner.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Our facilities combine traditional craftsmanship with modern
                  machinery, ensuring consistent quality at scale. Every product is
                  crafted with a strong focus on durability, print precision, and
                  responsible sourcing of raw materials.
                </p>
                <ul className="space-y-3">
                  {[
                    "OEM & private label manufacturing for global brands.",
                    "In-house design, sampling, printing, and finishing.",
                    "Dedicated export logistics team for on-time shipments.",
                    "Compliance with international quality and ethical standards.",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="mt-1 w-5 h-5 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-forest" />
                      </div>
                      <span className="text-sm md:text-base text-muted-foreground">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <p className="text-xs font-semibold tracking-[0.22em] uppercase text-muted-foreground">
                  Key Strengths
                </p>
                <div className="space-y-4">
                  {[
                    {
                      title: "Sustainable Materials",
                      description:
                        "Jute and cotton sourced with a focus on renewability and traceability.",
                      icon: Leaf,
                    },
                    {
                      title: "Modern Infrastructure",
                      description:
                        "Blending skilled artisanship with automated cutting, stitching, and finishing lines.",
                      icon: Factory,
                    },
                    {
                      title: "Global Export Readiness",
                      description:
                        "Documentation, certifications, and logistics support tailored for international buyers.",
                      icon: Globe2,
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex gap-3 rounded-2xl border border-sand/30 bg-cream/60 px-4 py-3"
                    >
                      <div className="mt-1 w-8 h-8 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-forest" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-forest">
                          {item.title}
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 md:py-20 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { number: "18+", label: "Years of Manufacturing Excellence", icon: Award },
              { number: "20+", label: "Countries Served Across the Globe", icon: Globe2 },
              { number: "500+", label: "Long-Term Export Clients", icon: Users },
              { number: "1M+", label: "Bags Produced Every Month", icon: Factory },
            ].map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 md:p-7 border border-sand/20 shadow-card flex flex-col gap-3 items-start"
              >
                <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-forest" />
                </div>
                <p className="font-heading text-3xl md:text-4xl font-bold text-forest">
                  {stat.number}
                </p>
                <p className="text-sm md:text-base text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-white rounded-2xl p-8 border border-sand/30 shadow-card space-y-4">
              <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-forest" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-forest">
                Our Mission
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                To enable brands worldwide to shift to eco-friendly packaging by
                manufacturing premium jute and cotton bags that are beautiful,
                durable, and responsibly made. We aim to create value for our clients,
                craftsmen, and communities through ethical and sustainable practices.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-sand/30 shadow-card space-y-4">
              <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                <Globe2 className="w-5 h-5 text-forest" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-forest">
                Our Vision
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                To be recognized as the world&apos;s most trusted partner for
                sustainable bag manufacturing—setting benchmarks in quality, design,
                and environmental stewardship while inspiring a global transition
                away from single-use plastics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="bg-white/80 rounded-3xl shadow-card border border-sand/30 px-6 md:px-10 lg:px-12 py-10 md:py-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest text-center mb-4">
              Why Choose Reliable Exports?
            </h2>
            <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
              A single manufacturing partner for all your eco-friendly bag requirements,
              combining thoughtful design, responsible sourcing, and export expertise.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Sustainable by Design",
                  description:
                    "We prioritize natural fibers, low-impact dyes, and waste-conscious production across every collection.",
                  icon: Leaf,
                },
                {
                  title: "Certified Quality & Compliance",
                  description:
                    "Processes aligned with international quality and social compliance standards for peace of mind.",
                  icon: ShieldCheck,
                },
                {
                  title: "Flexible Manufacturing",
                  description:
                    "From small custom runs to large-scale export orders, our lines are built to scale with your brand.",
                  icon: Factory,
                },
                {
                  title: "Global Export Expertise",
                  description:
                    "Experienced export team managing documentation, certifications, and global shipping routes.",
                  icon: Truck,
                },
                {
                  title: "Long-Term Partnerships",
                  description:
                    "500+ clients worldwide trust us for consistent quality, transparent communication, and reliability.",
                  icon: Handshake,
                },
                {
                  title: "Circular Mindset",
                  description:
                    "A continuous focus on reducing environmental footprint through recycling, re-use, and material innovation.",
                  icon: Recycle,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-cream-dark rounded-2xl p-6 border border-sand/30 shadow-card hover-lift transition-colors duration-300"
                >
                  <div className="w-10 h-10 bg-forest/10 rounded-full flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-forest" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-forest mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call To Action */}
      <section className="py-16 md:py-20 bg-forest">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
            Partner With Reliable Exports Today
          </h2>
          <p className="text-sm md:text-base text-cream/90 max-w-2xl mx-auto mb-8">
            Let&apos;s co-create eco-friendly packaging solutions that strengthen your
            brand, delight your customers, and respect the planet.
          </p>
          <Link to="/contact">
            <Button className="bg-sand hover:bg-sand-dark text-white font-medium rounded-full px-8 py-6 transition-all duration-300">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
